import { css } from '@emotion/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { omit } from 'underscore';

import t from 'react-translate';
import { AngularContext } from 'react-app';

import { RootState } from 'redux/schemas';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { ActivityProgress } from 'redux/schemas/models/activity';
import { NInPersonEventActivity, AttendeeInfoStatus } from 'redux/schemas/models/in-person-event';
import { useAppDispatch } from 'redux/store';
import { inPersonEventCheckIn } from 'redux/actions/in-person-events';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import LectureComponentProvider, { useLectureComponentContext } from 'components/lecture-component-provider';
import ActivityStatus from 'shared/components/activity/activity-status';
import ActivityTitle from 'shared/components/activity/activity-title';
import LectureComponentHeaderInput from 'shared/components/lecture-component-header-input';
import { LectureComponentProps, LecturePageMode } from 'lecture_pages/components';
import NvFroala from 'froala/components/nv-froala';
import { sanitize, SanitizationLevel } from 'froala/helpers/sanitizer';
import { primary } from 'styles/global_defaults/colors';
import ActivityPoints from 'shared/components/activity/activity-points';
import { InPersonEventContext } from './in-person-event-context';
import { useUpdateInPersonEvent, getInPersonEventSessionStates, getDisplayStatus } from './in-person-event-utils';
import InPersonEventAdminActions from './components/in-person-event-admin-actions';
import InPersonEventForm from './components/in-person-event-form';
import InPersonEventCard from './components/in-person-event-card';
import WillAttendCta from './components/will-attend-cta';

const containerStyle = css`
  .fr-placeholder {
    border: none !important;
  }
`;

const InPersonEventComponentContextWrapper = (
  props: LectureComponentProps<ComponentType.IN_PERSON_EVENT>,
) => {
  const isEdit = props.mode === LecturePageMode.EDIT;
  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);

  return (
    <LectureComponentProvider
      catalogId={catalogId}
      lectureComponentId={props.lectureComponent.id}
      lecturePageId={props.currentLecture.id}
      isEdit={isEdit}
      angularComponent={props.angularComponent}
    >
      <InPersonEventLectureComponent {...props} />
    </LectureComponentProvider>
  );
};

const InPersonEventLectureComponent = (
  props: LectureComponentProps<ComponentType.IN_PERSON_EVENT>,
) => {
  const { isEdit, catalogId, lectureComponentId } = useLectureComponentContext();
  const { injectServices } = useContext(AngularContext);
  const [CurrentUserManager, $state, $timeout] = injectServices(['CurrentUserManager', '$state', '$timeout']);
  const dispatch = useAppDispatch();
  const inPersonEvent = useSelector((state: RootState) => (
    state.models.inPersonEvents[props.lectureComponent?.inPersonEvent]));
  const sessionId = inPersonEvent?.eventSessions?.[0];

  // Get session data for the title
  const session = useSelector((state: RootState) => (
    sessionId ? state.models.inPersonEventSessions[sessionId] : null
  ));

  const inPersonEventRef = useRef(inPersonEvent);
  inPersonEventRef.current = inPersonEvent;

  // Track if check-in has been attempted to prevent duplicate API calls
  const checkInAttemptedRef = useRef(false);

  const [title, setTitle] = useState<string>(inPersonEvent?.title ?? '');
  const [mainDescription, setMainDescription] = useState<string>(inPersonEvent?.description ?? '');

  const updateInPersonEvent = useUpdateInPersonEvent();

  const sessionStates = getInPersonEventSessionStates(
    session?.startTime ?? null,
    session?.endTime ?? null,
  );

  const saveEvent = (updates: Partial<NInPersonEventActivity>) => {
    const prev = omit(inPersonEventRef.current, 'inPersonEvent');
    const merged = { ...prev, ...updates, eventSessions: [session] };
    updateInPersonEvent(merged);
  };

  const progress: ActivityProgress = inPersonEvent?.progress ?? session?.status ?? AttendeeInfoStatus.NOT_AVAILABLE;
  const displayStatus = getDisplayStatus(progress, sessionStates) as ActivityProgress;
  const isAdmin = CurrentUserManager.isAdmin();


  // Handle QR code check-in when arriving with check-in params in URL.
  // Protection against duplicate check-ins:
  // 1. checkInAttemptedRef - prevents re-execution on re-renders within same mount
  // 2. Immediate clearCheckInParams - removes URL params to prevent re-execution on remount
  useEffect(() => {
    const { checkInEventId, checkInPasscode } = $state.params;
    if (!checkInEventId || !checkInPasscode || sessionId !== Number(checkInEventId)) {
      return;
    }

    const clearCheckInParams = () => {
      $timeout(() => {
        $state.go($state.current, {
          checkInEventId: null,
          checkInPasscode: null,
        },
        { notify: false });
      });
    };

    // Already attempted in this mount - clear any stale params and exit
    if (checkInAttemptedRef.current) {
      clearCheckInParams();
      return;
    }

    checkInAttemptedRef.current = true;

    // Clear params immediately to prevent duplicate check-ins if component
    // remounts before the API call completes
    clearCheckInParams();

    const alreadyCheckedIn = session?.status === AttendeeInfoStatus.CHECK_IN_QR_CODE
      || session?.status === AttendeeInfoStatus.CHECK_IN_MANUAL
      || session?.status === AttendeeInfoStatus.COMPLETED;

    if (alreadyCheckedIn) {
      dispatch(addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS_BANG(),
        message: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.ALREADY_CHECKED_IN(),
      }));
      return;
    }

    dispatch(inPersonEventCheckIn({
      catalogId,
      sessionId: Number(checkInEventId),
      passcode: checkInPasscode,
    }))
      .unwrap()
      .then(() => {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.QR_CHECK_IN_SUCCESS(session?.title ?? ''),
        }));
      })
      .catch(() => {
        // Error alert handled by action
      });
  }, [sessionId, catalogId, dispatch, session?.title, $state, session?.status, $timeout]);

  const contextValue = {
    inPersonEvent,
    catalogId,
    isEdit,
    isAdmin,
    sessionId,
  };

  return (
    <InPersonEventContext.Provider value={contextValue}>
      <div
        className='d-flex flex-column align-items-center mb-4 w-100 border-bottom border-gray-5 pb-4'
        id={`lecture-component-${lectureComponentId}`}
        css={containerStyle}
      >
        <ActivityStatus
          icon='work'
          status={displayStatus}
          statusesIcons={{
            not_available: 'work',
            is_confirmed: 'work',
            check_in_manually: 'work',
            check_in_qr_code: 'work',
            in_progress: 'work',
          }}
        />
        <ActivityTitle
          status={displayStatus}
          className='mt-2'
          statusesTexts={{
            not_available: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.STATUS.ATTEND(),
            is_confirmed: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.STATUS.ATTEND(),
            check_in_manually: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.STATUS.CHECKED_IN(),
            check_in_qr_code: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.STATUS.CHECKED_IN(),
            in_progress: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.STATUS.IN_PROGRESS(),
            completed: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.STATUS.ATTENDED(),
            missed: t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.STATUS.MISSED(),
          }}
          statusesColors={{
            not_available: primary,
            is_confirmed: primary,
            check_in_manually: primary,
            check_in_qr_code: primary,
            in_progress: primary,
          }}
        />
        <LectureComponentHeaderInput
          value={title}
          editable={isEdit}
          onChange={setTitle}
          onBlur={() => {
            if (title !== inPersonEvent?.title) {
              saveEvent({ title });
            }
          }}
          required
        />
        {inPersonEvent?.pointsConfiguration && (
        <div className='dot-separator mt-2'>
          <ActivityPoints
            pointsReceived={inPersonEvent.pointsReceived}
            totalPoints={inPersonEvent.pointsConfiguration?.points}
          />
        </div>
        )}
        <div className='w-100 mb-4 mt-2'>
          {isEdit ? (
            <NvFroala
              value={mainDescription}
              onChange={setMainDescription}
              onBlur={() => {
                if (mainDescription !== inPersonEvent?.description) {
                  saveEvent({ description: mainDescription });
                }
              }}
              placeholder={t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.ADD_EXPECTATIONS()}
              className='border border-gray-4'
            />
          ) : (
            <div
              className='practice-text text-body'
            // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitize(mainDescription, SanitizationLevel.BASIC) }}
            />
          )}
        </div>

        {!isEdit && (
          <InPersonEventCard />
        )}

        {isEdit && (
          <InPersonEventForm />
        )}
        {(displayStatus === AttendeeInfoStatus.NOT_AVAILABLE || displayStatus === AttendeeInfoStatus.IS_CONFIRMED) && (
          <WillAttendCta
            released={props.currentLecture.released}
            progress={progress}
          />
        )}

        {isAdmin && props.currentLecture.released && !isEdit && (
          <InPersonEventAdminActions />
        )}
      </div>
    </InPersonEventContext.Provider>
  );
};

export default InPersonEventComponentContextWrapper;
